import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["onMousedown"]

import { ref, computed, watch, onBeforeUnmount, toRef, nextTick, type ComponentPublicInstance } from 'vue';
import { useFloating, size, offset, autoUpdate, type Placement } from '@floating-ui/vue';
import {
  registerPopup,
  unregisterPopup,
  currentPopupId,
  isCurrentPopup,
} from './selectorPopupRegistry';

type TriggerRef = HTMLElement | ComponentPublicInstance | null | undefined;
type TeleportTarget = string | HTMLElement;


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectorPopup',
  props: {
    visible: { type: Boolean },
    triggerEl: { default: undefined },
    teleportTo: { default: 'body' },
    defaultWidth: { default: 300 },
    wideLayout: { type: Boolean, default: false },
    wideDefaultWidth: { default: 560 },
    minWidth: { default: 200 },
    maxWidth: { default: 2000 },
    minHeight: { default: 150 },
    maxHeight: { default: 500 }
  },
  emits: ["update:visible"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const myId = Symbol('selector-popup');
const teleportTarget = computed<TeleportTarget>(() => props.teleportTo || 'body');

const referenceEl = computed((): HTMLElement | null => {
  const el = props.triggerEl;
  if (!el) return null;
  if (el instanceof HTMLElement) return el;
  if (typeof el === 'object' && '$el' in el)
    return (el as ComponentPublicInstance).$el as HTMLElement;
  return null;
});

const floatingEl = ref<HTMLElement | null>(null);
const contentEl = ref<HTMLElement | null>(null);
const contentMinWidth = ref(0);

const userWidth = ref(props.defaultWidth);
const userHeight = ref<number | null>(null);
const availableMaxHeight = ref(600);
const lockedPlacement = ref<Placement>('bottom-start');

function updateContentMinSize() {
  // Не замеряем scrollWidth — при min-width:100% на контенте он равен
  // ширине попапа и блокирует сужение. Используем props.minWidth как пол.
  contentMinWidth.value = 0;
}

function computeBestPlacement(refEl: HTMLElement): Placement {
  const rect = refEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceAbove = rect.top;
  const spaceBelow = vh - rect.bottom;
  const spaceLeft = rect.left;
  const spaceRight = vw - rect.right;

  const vertical = spaceBelow >= spaceAbove ? 'bottom' : 'top';
  const horizontal = spaceRight >= spaceLeft ? 'right' : 'left';

  const maxVertical = Math.max(spaceAbove, spaceBelow);
  const maxHorizontal = Math.max(spaceLeft, spaceRight);

  if (maxVertical >= maxHorizontal) {
    const align = spaceRight >= spaceLeft ? 'start' : 'end';
    return `${vertical}-${align}` as Placement;
  }
  const align = spaceBelow >= spaceAbove ? 'start' : 'end';
  return `${horizontal}-${align}` as Placement;
}

const openRef = toRef(props, 'visible');

const { floatingStyles, placement, isPositioned } = useFloating(
  referenceEl,
  floatingEl,
  {
    strategy: 'fixed',
    placement: lockedPlacement,
    open: openRef,
    middleware: [
      offset(4),
      size({
        apply({ availableHeight }) {
          availableMaxHeight.value = Math.max(
            props.minHeight,
            availableHeight - 16,
          );
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  },
);

const effectiveMinWidth = computed(() =>
  Math.max(props.minWidth, Math.min(contentMinWidth.value || 0, props.maxWidth)),
);

const effectiveHeight = computed(() => {
  if (userHeight.value != null) return userHeight.value;
  return Math.min(availableMaxHeight.value, props.maxHeight);
});

const contentHeight = computed(() => {
  return Math.max(0, effectiveHeight.value - 2);
});

const popupStyles = computed((): Record<string, string> => ({
  ...(floatingStyles.value as unknown as Record<string, string>),
  width: `${userWidth.value}px`,
  height: `${effectiveHeight.value}px`,
  minWidth: `${effectiveMinWidth.value}px`,
  minHeight: `${props.minHeight}px`,
}));

function getAnchorCorner(p: string): string {
  const parts = (p || 'bottom-start').split('-');
  const side = parts[0];
  const align = parts[1] || 'start';

  if (side === 'right') return align === 'start' ? 'tl' : 'bl';
  if (side === 'left') return align === 'start' ? 'tr' : 'br';
  if (side === 'bottom') return align === 'start' ? 'tl' : 'tr';
  if (side === 'top') return align === 'start' ? 'bl' : 'br';
  return 'tl';
}

const allCorners = ['tl', 'tr', 'bl', 'br'] as const;

const freeCorners = computed(() => {
  const anchor = getAnchorCorner(placement.value);
  return allCorners.filter((c) => c !== anchor);
});

// --- Resize ---
const isResizing = ref(false);
let resizeStartX = 0;
let resizeStartY = 0;
let resizeStartWidth = 0;
let resizeStartHeight = 0;
let resizeCorner = '';

function startResize(corner: string, e: MouseEvent) {
  isResizing.value = true;
  resizeCorner = corner;
  resizeStartX = e.clientX;
  resizeStartY = e.clientY;
  resizeStartWidth = userWidth.value;
  resizeStartHeight =
    userHeight.value ?? floatingEl.value?.offsetHeight ?? 300;
  document.addEventListener('mousemove', onResizeMove);
  document.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(e: MouseEvent) {
  const dx = e.clientX - resizeStartX;
  const dy = e.clientY - resizeStartY;
  const xMult = resizeCorner.includes('r') ? 1 : -1;
  const yMult = resizeCorner.includes('b') ? 1 : -1;
  userWidth.value = Math.max(
    effectiveMinWidth.value,
    Math.min(props.maxWidth, resizeStartWidth + dx * xMult),
  );
  userHeight.value = Math.max(
    props.minHeight,
    Math.min(
      availableMaxHeight.value,
      resizeStartHeight + dy * yMult,
    ),
  );
}

function onResizeEnd() {
  isResizing.value = false;
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
}

// --- Close ---
function close() {
  emit('update:visible', false);
}

function onDocumentMousedown(e: MouseEvent) {
  if (!props.visible) return;
  const target = e.target as Node;
  if (floatingEl.value?.contains(target)) return;
  const ref = referenceEl.value;
  if (ref?.contains(target)) return;

  e.stopImmediatePropagation();
  e.stopPropagation();

  // Закрытие «снаружи»: mousedown глушим, но q-dialog часто закрывается по click на backdrop.
  // Одноразовый capture click — первый клик закрывает только селектор, второй — уже диалог.
  const stopNextClick = (ce: Event) => {
    ce.stopImmediatePropagation();
    ce.stopPropagation();
    document.removeEventListener('click', stopNextClick, true);
  };
  document.addEventListener('click', stopNextClick, true);
  setTimeout(() => document.removeEventListener('click', stopNextClick, true), 500);

  close();
}

function onDocumentKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.visible) {
    e.stopPropagation();
    close();
  }
}

// --- Singleton ---
watch(currentPopupId, () => {
  if (props.visible && !isCurrentPopup(myId)) {
    close();
  }
});

// --- Visibility lifecycle ---
watch(
  () => props.visible,
  (val) => {
    if (val) {
      registerPopup(myId);
      userWidth.value = props.wideLayout ? props.wideDefaultWidth : props.defaultWidth;
      userHeight.value = null;
      nextTick(() => {
        updateContentMinSize();
      });
      const refEl = referenceEl.value;
      if (refEl) {
        lockedPlacement.value = computeBestPlacement(refEl);
      }
      document.addEventListener('mousedown', onDocumentMousedown, true);
      document.addEventListener('keydown', onDocumentKeydown, true);
    } else {
      unregisterPopup(myId);
      document.removeEventListener('mousedown', onDocumentMousedown, true);
      document.removeEventListener('keydown', onDocumentKeydown, true);
    }
  },
);

onBeforeUnmount(() => {
  unregisterPopup(myId);
  document.removeEventListener('mousedown', onDocumentMousedown, true);
  document.removeEventListener('keydown', onDocumentKeydown, true);
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: teleportTarget.value }, [
    (_ctx.visible)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "floatingEl",
          ref: floatingEl,
          class: _normalizeClass(["selector-popup", {
        'selector-popup--positioned': _unref(isPositioned),
        'selector-popup--resizing': isResizing.value,
      }]),
          style: _normalizeStyle(popupStyles.value),
          onFocusin: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(freeCorners.value, (corner) => {
            return (_openBlock(), _createElementBlock("div", {
              key: corner,
              class: _normalizeClass([
          'selector-popup__resize-handle',
          `selector-popup__resize-handle--${corner}`,
        ]),
              onMousedown: _withModifiers(($event: any) => (startResize(corner, $event)), ["prevent"])
            }, _cache[1] || (_cache[1] = [
              _createElementVNode("svg", {
                viewBox: "0 0 10 10",
                class: "selector-popup__resize-icon"
              }, [
                _createElementVNode("path", {
                  d: "M3 3L7 7M5 7H7V5",
                  stroke: "currentColor",
                  "stroke-width": "1.5",
                  fill: "none",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                })
              ], -1)
            ]), 42, _hoisted_1))
          }), 128)),
          _createElementVNode("div", {
            class: "selector-popup__content",
            ref_key: "contentEl",
            ref: contentEl
          }, [
            _renderSlot(_ctx.$slots, "default", { contentHeight: contentHeight.value })
          ], 512)
        ], 38))
      : _createCommentVNode("", true)
  ], 8, ["to"]))
}
}

})